import { AlertDefaults, DirectorySettings, RecipientSettings, FeatureLayerSettings, AlertType, AccessRecipe } from 'src/app/hickory-api';
import { DirectoryCache } from 'src/app/shared/directory/base/directory-cache';
import { Directory, DirectoryGroup, DirectoryUser } from 'src/app/shared/directory/base/models';
import { deepCopy, isInteger, isString, removeDuplicates, toInteger } from 'src/app/shared/model/utility';
import { AlertModelService } from '../alert-model.service';
import { AlertGeofencesHelper } from './alert-geofences-helper';
import { AccessRecipeHelper } from 'src/app/shared/model/accessrecipe-helper';
import { AlertRecipientsSearchingHelper } from './alert-recipients-searching-helper';
export class AlertRecipientsHelper {

  static initRecipients(recipients?: RecipientSettings): RecipientSettings {
    if (!recipients) recipients = {};
    if (!recipients.contactIds) recipients.contactIds = [];
    if (!recipients.groupIds) recipients.groupIds = [];
    if (!recipients.mandatoryGroupIds) recipients.mandatoryGroupIds = [];
    if (!recipients.searchIds) recipients.searchIds = [];
    if (!recipients.directories) recipients.directories = [];
    if (!recipients.searchFilters) recipients.searchFilters = [];
    return recipients;
  }

  static initDirectory(directory?: DirectorySettings): DirectorySettings {
    if (!directory) directory = {};
    if (!directory.contactIds) directory.contactIds = [];
    if (!directory.groupIds) directory.groupIds = [];
    if (!directory.mandatoryGroupIds) directory.mandatoryGroupIds = [];
    return directory;
  }

  static postProcess(_service: AlertModelService, alert: AlertDefaults) {
    // ROAD-9326: remove individual recipient for polls
    if (alert?.alertType === AlertType.Poll && !!alert?.recipients?.individualNumber) {
      alert.recipients.individualNumber = undefined;
    }
  }

  static preparePreview(service: AlertModelService, preview: AlertDefaults) {
    // ROAD-9326: remove individual recipient for polls
    if (preview?.alertType === AlertType.Poll && !!preview?.recipients?.individualNumber) {
      preview.recipients.individualNumber = undefined;
    }

    // copy mandatoryGroupIds to groupIds
    const mandatoryIds = service?.alertMandatoryGroupIds || [];
    if (mandatoryIds?.length) {
      preview.recipients.groupIds = this.combinedLegacyGroupIds(service);
    }

    
    if (this.shouldApplyClassificationFilter(service)) {
      const directoryIds = service.directories.filter(d => Directory.isLegacy(d.id)).map(d => d.id);
      const searchFilters = deepCopy(service?.alertRecipients?.searchFilters ?? []);
      preview.recipients.searchFilters = [];

      for (const id of directoryIds) {
        const filters = searchFilters.filter(x => x.type === id)
          .map(x => AlertRecipientsSearchingHelper.withClassificationFilter(x, id, service.useSplitHickoryDirectory));

        preview.recipients.searchFilters.push(...filters);
      }
    }
  }

  private static shouldApplyClassificationFilter(service: AlertModelService): boolean {
    return !service.isEditingTemplate
        && service.useSplitHickoryDirectory 
        && service?.alertRecipients?.searchFilters?.some(x => AccessRecipeHelper.isValid(x));
  }

  static municipalsCount(service: AlertModelService): number {
    if (!service?.alertRecipients?.municipals?.sendTo?.length) return 0;
    return service?.alertRecipients?.municipals?.cities?.length ?? 0;
  }

  static validSearchCount(service: AlertModelService, recipients: RecipientSettings = service.alertRecipients, directoryId?: string): number {
    const validRecipes = recipients?.searchFilters?.filter(x => AccessRecipeHelper.isValid(x)) ?? [];
    if (!directoryId) {
      return validRecipes?.length ?? 0;
    }

    return validRecipes?.filter(x => AlertRecipientsSearchingHelper.isValidForDirectory(x, directoryId, service.useSplitHickoryDirectory)) ?.length ?? 0;
  }

  static combinedLegacyGroupIds(service: AlertModelService, recipients: RecipientSettings = service.alertRecipients): number[] {
    let ids = recipients?.groupIds?.slice() || [];
    const mandatoryIds = service?.alertMandatoryGroupIds || [];
    if (mandatoryIds?.length) {
      ids = ids.concat(mandatoryIds.filter(id => !ids.includes(id)));
      ids = removeDuplicates(ids);
    }

    return ids;
  }

  static combinedLegacyContactIds(service: AlertModelService, recipients: RecipientSettings = service.alertRecipients): number[] {
    return recipients?.contactIds?.slice() || [];
  }

  static combinedContactCount(service: AlertModelService, directoryId?: string): number {
    if (directoryId) {
      return this.allDirectoryUserIds(service, directoryId)?.length ?? 0;
    } else {
      const legacyCounts = this.combinedLegacyContactIds(service)?.length ?? 0;
      const directoryCounts = service.alertRecipients?.directories?.map(d => 
        d.contactIds?.length ?? 0)?.reduce((sum, current) => sum + current, 0) ?? 0;
      return legacyCounts + directoryCounts;
    }
  }

  static allDirectoryUserIds(service: AlertModelService, directoryId: string): string[] {
    if (Directory.isLegacy(directoryId)) {
      const legacyIds = this.combinedLegacyContactIds(service)?.map(id => '' + id) ?? [];
      if (service?.useSplitHickoryDirectory === true) {
        const cache = DirectoryCache.getInstance();
        const items = cache.getAllUsers(directoryId);
        return items?.filter(x => legacyIds.includes(x.id))?.map(x => x.id);
      } else {
        return legacyIds;
      }
    }
    
    const dir = service.alertRecipients?.directories?.find(d => d.directoryId === directoryId);
    return dir?.contactIds;
  }

  static combinedDirectoryGroupIds(directory: DirectorySettings): string[] {
    let ids = directory?.groupIds?.slice() || [];
    const mandatoryIds = directory?.mandatoryGroupIds || [];
    if (mandatoryIds?.length) {
      ids = ids.concat(mandatoryIds.filter(id => !ids.includes(id)))
      ids = removeDuplicates(ids);
    }

    return ids;
  }

  static allRecipientsDirectoryGroupIds(recipients: RecipientSettings): string[] {
    let ids = new Array<string>();
    recipients?.directories?.forEach(dir => {
      const dirIds = this.combinedDirectoryGroupIds(dir);
      ids = ids.concat(dirIds);
      ids = removeDuplicates(ids);
    });

    return ids;
  }

  static combinedGroupCount(service: AlertModelService, directoryId?: string, recipients?: RecipientSettings): number {
    recipients = recipients ?? service?.alertRecipients;
    if (directoryId) {
      return this.allDirectoryGroupIds(service, directoryId, recipients)?.length ?? 0;
    } else {
      const legacyCounts = this.combinedLegacyGroupIds(service, recipients)?.length ?? 0;
      const directoryCounts = recipients?.directories?.map(d => 
        this.combinedDirectoryGroupIds(d)?.length ?? 0)?.reduce((sum, current) => sum + current, 0) ?? 0;
      return legacyCounts + directoryCounts;
    }
  }

  static allDirectoryGroupIds(service: AlertModelService, directoryId, recipients?: RecipientSettings): string[] {
    if (Directory.isLegacy(directoryId)) {
      const legacyIds = this.combinedLegacyGroupIds(service)?.map(id => '' + id) ?? [];
      if (service?.useSplitHickoryDirectory === true) {
        const cache = DirectoryCache.getInstance();
        const items = cache.getAllGroups(directoryId);
        return items?.filter(x => legacyIds.includes(x.id))?.map(x => x.id);
      } else {
        return legacyIds;
      }
    }
    
    recipients = recipients ?? service.alertRecipients;
    const dir = recipients?.directories?.find(d => d.directoryId === directoryId);
    return this.combinedDirectoryGroupIds(dir);
  }

  static clearSelections(service: AlertModelService, directoryId) {
    const directory = service.getDirectory(directoryId);
    if (directory) {
      directory.totalRecipientCount = -1;
    }

    const recipients = service.alertRecipients;
    if (Directory.isLegacy(directoryId)) {
      recipients.contactIds = [];
      recipients.groupIds = [];
      recipients.searchFilters = [];
    }
    
    const dirSettings = service.alertRecipients?.directories?.find(d => d.directoryId === directoryId);
    if (dirSettings) {
      dirSettings.contactIds = [];
      dirSettings.groupIds = [];
    }
  }

  static validate(service: AlertModelService, recipients: RecipientSettings = service.alertRecipients): string[] {
    if (!!recipients.municipals?.cities?.length && !recipients.municipals?.sendTo?.length) return ['InvalidMunicipalSelectionNeedsSendTo'];
    if (!recipients.municipals?.cities?.length && !!recipients.municipals?.sendTo?.length) return ['InvalidMunicipalSelectionNeedsAreas'];

    return this.hasSelectedRecipients(service, recipients) ? [] : ['SelectRecipients'];
  }

  static hasSelectedRecipients(service: AlertModelService, recipients: RecipientSettings = service.alertRecipients): boolean {
    if (recipients.distributionListId) return true;
    if (!!recipients.directories?.some(d => !!d.contactIds?.length || d.groupIds?.length || d.mandatoryGroupIds?.length) === true) return true;
    if (recipients.contactIds?.length) return true;
    if (recipients.groupIds?.length) return true;
    if (service.alertMandatoryGroupIds?.length) return true;
    if (!!recipients.geofences && !!recipients.featureLayers?.length) return true;
    if (!!recipients.municipals?.cities?.length && !!recipients.municipals?.sendTo?.length) return true;
    if (recipients.searchFilters?.length && AccessRecipeHelper.areValid(recipients.searchFilters)) return true;

    // validate directory users
    const foundMultiDirectoryUsers = recipients.featureLayers?.filter(x => x.FeaturesSource === FeatureLayerSettings.FeaturesSourceEnum.MultiDirectoryUsers);
    if (foundMultiDirectoryUsers != null && foundMultiDirectoryUsers.length > 0) {
      const foundDirectoryCount = foundMultiDirectoryUsers.find(x => x.Count != null && x.Count > 0);
      if (foundDirectoryCount != null && foundDirectoryCount.Count > 0) {
        return true;
      }
    }

    return false;
  }

  static summaryText(service: AlertModelService): string {
    const adHocCount = service.adHocList ? 1 : 0;
    const groupCount = this.combinedGroupCount(service);
    const contactCount = this.combinedContactCount(service);
    const municipalsCount = this.municipalsCount(service);
    const searchesCount = this.validSearchCount(service);
    const mapAreasCount = AlertGeofencesHelper.geofencesCount(service);
    const text: string[] = [];
    if (adHocCount) {
      text.push(adHocCount + ' ' + ((adHocCount > 1) ? 'Ad Hoc Lists' : 'Ad Hoc List'));
    }
    if (groupCount) {
      text.push(groupCount + ' ' + ((groupCount > 1) ? 'Groups' : 'Group'));
    }
    if (contactCount) {
      text.push(contactCount + ' ' + ((contactCount > 1) ? 'Contacts' : 'Contact'));
    }
    if (municipalsCount) {
      text.push(municipalsCount + ' ' + ((municipalsCount > 1) ? 'Cities' : 'City'));
    }
    if (mapAreasCount) {
      text.push(mapAreasCount + ' ' + ((mapAreasCount > 1) ? 'Map Areas' : 'Map Area'));
    }
    if (searchesCount) {
      text.push(searchesCount + ' ' + ((searchesCount > 1) ? 'Searches' : 'Search'));
    }
    
    return text.length ? text.join(', ') : 'None Selected';
  }

  static isGroupIdSelected(groupId: string | number, service: AlertModelService, recipients: RecipientSettings = service.alertRecipients): boolean {
    if (!groupId || groupId === 'null') return false;
    if (isString(groupId)) {
      const strId = '' + groupId;
      for (const d of recipients?.directories ?? []) {
        if (d.groupIds?.includes(strId)) return true;
      }
    }
    if (isInteger(groupId)) {
      const intId = toInteger(groupId);
      if (recipients?.groupIds?.includes(intId)) return true;
    }
    return this.isGroupIdRequired(groupId, service, recipients);
  }

  static isGroupIdRequired(groupId: string | number, service: AlertModelService, recipients: RecipientSettings = service.alertRecipients): boolean {
    if (!groupId || groupId === 'null') return false;
    if (isString(groupId)) {
      const strId = '' + groupId;
      for (const d of recipients?.directories ?? []) {
        if (d.mandatoryGroupIds?.includes(strId)) return true;
      }
    }
    if (isInteger(groupId)) {
      const intId = toInteger(groupId);
      if (recipients?.mandatoryGroupIds?.includes(intId)) return true;
    }
    return false;
  }

  static isContactIdSelected(contactId: string | number, service: AlertModelService, recipients: RecipientSettings = service.alertRecipients): boolean {
    if (!contactId || contactId === 'null') return false;
    if (isString(contactId)) {
      const strId = '' + contactId;
      for (const d of recipients?.directories ?? []) {
        if (d.contactIds?.includes(strId)) return true;
      }
    }
    if (isInteger(contactId)) {
      const intId = parseInt('' + contactId);
      if (recipients?.contactIds?.includes(intId)) return true;
    }
    return false;
  }

  static selectGroupChange(item: DirectoryGroup, selected: boolean, service: AlertModelService, recipients: RecipientSettings = service.alertRecipients) {
    if (!item || !recipients) return;
    if (item.hickoryId) {
      const model = recipients;
      const id = item.hickoryId;
      const ids = model.groupIds ?? [];
      if (selected) {
        if (!ids.includes(id)) ids.push(id);
        model.groupIds = ids;
      } else if (!selected) {
        ids.splice(ids.indexOf(id), 1);
      }
    } else if (item.directoryId) {
      const dirId = item.directoryId;
      const model = this.getOrCreateDirectorySettingsWithId(dirId, recipients);
      if (!model) return;
      const id = item.id;
      const ids = model.groupIds ?? [];
      if (selected) {
        if (!ids.includes(id)) ids.push(id);
        model.groupIds = ids;
      } else {
        model.groupIds = ids.filter(i => i !== id);
      }
    }
  }

  static selectUserChange(item: DirectoryUser, selected: boolean, service: AlertModelService, recipients: RecipientSettings = service.alertRecipients) {
    if (!item || !recipients) return;
    if (item.hickoryId) {
      const model = recipients;
      const id = item.hickoryId;
      const ids = model.contactIds ?? [];
      if (selected) {
        if (!ids.includes(id)) ids.push(id);
        model.contactIds = ids;
      } else if (!selected) {
        model.contactIds = ids.filter(i => i !== id);
      }
    } else if (item.directoryId) {
      const dirId = item.directoryId;
      const model = this.getOrCreateDirectorySettingsWithId(dirId, recipients);
      if (!model) return;
      const id = item.id;
      const ids = model.contactIds ?? [];
      if (selected) {
        if (!ids.includes(id)) ids.push(id);
        model.contactIds = ids;
      } else {
        model.contactIds = ids.filter(i => i !== id);
      }
    }
  }

  static updateSearchFilterChange(items: AccessRecipe[], service: AlertModelService, recipients: RecipientSettings = service.alertRecipients) {
    if (!recipients) return;
    recipients.searchFilters = items ?? [];
  }

  static getOrCreateDirectorySettingsWithId(directoryId: string, recipients: RecipientSettings): DirectorySettings {
    if (!directoryId || !recipients) return null;
    let dir = recipients.directories.find(x => x.directoryId === directoryId);
    if (!dir) {
      dir = this.initDirectory({ directoryId: directoryId });
      if (!recipients.directories) recipients.directories = [];
      recipients.directories.push(dir);
    }

    return dir;
  }

  static adHocRecipientsCount(service: AlertModelService): number {
    if (!service?.adHocList?.id) return 0;
    if (service?.alertRecipients?.distributionListId !== service?.adHocList?.id) return 0;
    return service?.adHocList?.recipientCount ?? 0;
  }
}
